import React, { useState, useEffect } from 'react';

function getDatesBetween(start, end) {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const dates = [];

  while (startDate <= endDate) {
    dates.push(new Date(startDate));
    startDate.setDate(startDate.getDate() + 1);
  }

  return dates.reverse();
}

const GameModals = () => {
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isPrivate: false,
    groups: [
      { answers: '', description: '' },
      { answers: '', description: '' },
      { answers: '', description: '' },
      { answers: '', description: '' }
    ]
  });

  const [games, setGames] = useState({ games: [], pagination: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [archivePage, setArchivePage] = useState(1);
  const [activeTab, setActiveTab] = useState('archive');
  const [loading, setLoading] = useState(false);
  const archiveDates = getDatesBetween('6/12/2023', new Date());
  const archivePageSize = 20;
  const archiveTotalPages = Math.ceil(archiveDates.length / archivePageSize);
  const visibleArchiveDates = archiveDates.slice(
    (archivePage - 1) * archivePageSize,
    archivePage * archivePageSize
  );

  const fetchGames = async (sortBy = 'popular') => {
    setLoading(true);
    try {
      const response = await fetch(`https://gg-apis.vercel.app/custom-connections/games?sort=${sortBy}&page=${currentPage}&search=${searchTerm}`);
      const data = await response.json();
      setGames(data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
    setLoading(false);
  };

  // Fetch games whenever modal is shown or when search/filter/page changes
  useEffect(() => {
    if (showListModal && activeTab === 'community') {
      fetchGames('popular');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showListModal, currentPage, searchTerm, activeTab]);

  const handleInputChange = (e, groupIndex = null, fieldName = null) => {
    setError('');
    if (groupIndex !== null) {
      const updatedGroups = formData.groups.map((group, index) => {
        if (index === groupIndex) {
          return { ...group, [fieldName]: e.target.value };
        }
        return group;
      });
      setFormData({ ...formData, groups: updatedGroups });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  useEffect(() => {
    const customBtns = document.getElementsByClassName('create-custom');
    const listBtns = document.getElementsByClassName('games-list');
    
    // Modified to show modal first, then fetch data
    const handleListClick = () => {
      setActiveTab('archive');
      setArchivePage(1);
      setCurrentPage(1);
      setSearchTerm('');
      setShowListModal(true);
      // fetchGames will be called by the useEffect when showListModal becomes true
    };

    // Convert HTMLCollection to Array for easier handling
    const customElements = Array.from(customBtns);
    const listElements = Array.from(listBtns);

    // Add event listeners to all elements with those classes
    customElements.forEach(btn => {
      btn.addEventListener('click', () => setShowCustomModal(true));
    });
    
    listElements.forEach(btn => {
      btn.addEventListener('click', handleListClick);
    });
    
    // Cleanup
    return () => {
      customElements.forEach(btn => {
        btn.removeEventListener('click', () => {});
      });
      listElements.forEach(btn => {
        btn.removeEventListener('click', handleListClick);
      });
    };
  }, []); // Removed activeTab from dependencies since it's not used in the effect anymore

  const handleSubmit = async () => {
    try {
      if (!formData.title || !formData.author) {
        setError('Title and author are required');
        return;
      }

      for (let i = 0; i < formData.groups.length; i++) {
        const group = formData.groups[i];
        const answers = group.answers.split(',').map(item => item.trim());
        if (answers.length !== 4 || !group.description) {
          setError(`Group ${i + 1} must have exactly 4 items and a description`);
          return;
        }
      }

      const formattedData = {
        title: formData.title,
        author: formData.author,
        isPrivate: formData.isPrivate,
        groups: {},
        startingGroups: [],
        played: 0,
        slug: formData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      };

      formData.groups.forEach((group, index) => {
        formattedData.groups[group.description] = {
          level: index,
          members: group.answers.split(',').map(item => item.trim())
        };
      });

      const allWords = formData.groups.flatMap(group => 
        group.answers.split(',').map(item => item.trim())
      );
      const shuffled = [...allWords].sort(() => Math.random() - 0.5);
      formattedData.startingGroups = Array(4).fill().map((_, i) => 
        shuffled.slice(i * 4, (i + 1) * 4)
      );

      const response = await fetch('https://gg-apis.vercel.app/custom-connections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create game');
        return;
      }

      const data = await response.json();
      window.location.href = `/?custom=${data.slug}`;
    } catch (error) {
      setError('Error creating game. Please try again.');
      console.error('Error creating game:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === 'archive') {
      setArchivePage(1);
      return;
    }

    setCurrentPage(1);
  };

  return (
    <div className="modals-container">
      {showCustomModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create Custom Game</h2>
              <button className="close-btn" onClick={() => setShowCustomModal(false)}>×</button>
            </div>
            <div className="modal-content">
              {error && <div className="error-message">{error}</div>}
              <input
                type="text"
                placeholder="Title"
                name="title"
                value={formData.title}
                onChange={(e) => handleInputChange(e)}
              />
              <input
                type="text"
                placeholder="Author"
                name="author"
                value={formData.author}
                onChange={(e) => handleInputChange(e)}
              />
              
              <div className="privacy-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.isPrivate}
                    onChange={(e) => setFormData({
                      ...formData,
                      isPrivate: e.target.checked
                    })}
                  />
                  <span className="slider"></span>
                </label>
                <span className="toggle-label">
                  {formData.isPrivate ? 'Private' : 'Public'} Game
                </span>
              </div>

              {formData.groups.map((group, index) => (
                <div key={index} className="group-inputs">
                  <input
                    type="text"
                    placeholder={`Group ${index + 1} Answers (comma-separated)`}
                    value={group.answers}
                    onChange={(e) => handleInputChange(e, index, 'answers')}
                  />
                  <input
                    type="text"
                    placeholder={`Group ${index + 1} Description`}
                    value={group.description}
                    onChange={(e) => handleInputChange(e, index, 'description')}
                  />
                </div>
              ))}
              
              <button className="submit-btn" onClick={handleSubmit}>
                Create Game
              </button>
            </div>
          </div>
        </div>
      )}

      {showListModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Browse Games</h2>
              <button className="close-btn" onClick={() => setShowListModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="tabs">
                <button
                  className={`tab ${activeTab === 'archive' ? 'active' : ''}`}
                  onClick={() => handleTabChange('archive')}
                >
                  Archive Games
                </button>
                <button
                  className={`tab ${activeTab === 'community' ? 'active' : ''}`}
                  onClick={() => handleTabChange('community')}
                >
                  Community
                </button>
              </div>

              {activeTab === 'community' && (
                <input
                  type="text"
                  placeholder="Search games..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="search-input"
                />
              )}

              {activeTab === 'archive' ? (
                <>
                  <div className="games-list">
                    {visibleArchiveDates.map((date) => {
                      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

                      return (
                        <div
                          key={formattedDate}
                          className="game-card"
                          onClick={() => {
                            window.location.href = `/?archive=${formattedDate}`;
                          }}
                        >
                          <h3>Connections Archive {formattedDate}</h3>
                          <p>Open this archived puzzle and play that day&apos;s game.</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pagination">
                    <button
                      onClick={() => setArchivePage(p => Math.max(1, p - 1))}
                      disabled={archivePage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {archivePage}</span>
                    <button
                      onClick={() => setArchivePage(p => Math.min(archiveTotalPages, p + 1))}
                      disabled={archivePage >= archiveTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="games-list">
                    {loading ? (
                      <div className="loading">Loading...</div>
                    ) : (
                      games.games?.map((game) => (
                        <div
                          key={game._id}
                          className="game-card"
                          onClick={() => window.location.href = `/?custom=${game.slug}`}
                        >
                          <h3>{game.title}</h3>
                          <p>by {game.author} • Played {game.played} times</p>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {currentPage}</span>
                    <button
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={!games.pagination || currentPage >= games.pagination.pages}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modals-container {
          position: relative;
          z-index: 1000;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 80vh;
          overflow-y: auto;
          margin-top:-90px;
        }

        @media(max-width:600px){
            .modal{
            max-height:70vh;
            
            }
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-content {
          padding: 20px;
        }

        .modals-container .error-message {
          color: #dc3545;
          padding: 10px;
          margin-bottom: 10px;
          border: 1px solid #dc3545;
          border-radius: 4px;
          background-color: #f8d7da;
        }

        .modals-container .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          color: #666;
        }

        .modals-container input {
          width: 100%;
          padding: 8px 12px;
          margin-bottom: 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .modals-container .group-inputs {
          margin-bottom: 16px;
        }

        .modals-container .submit-btn {
          width: 100%;
          padding: 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .modals-container .submit-btn:hover {
          background: #0056b3;
        }

        .privacy-toggle {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
          padding: 8px 0;
        }

        .toggle-label {
          margin-left: 12px;
          color: #666;
        }

        .switch {
          position: relative;
          display: inline-block;
          width: 60px;
          height: 34px;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 34px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 26px;
          width: 26px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }

        input:checked + .slider {
          background-color: #007bff;
        }

        input:focus + .slider {
          box-shadow: 0 0 1px #007bff;
        }

        input:checked + .slider:before {
          transform: translateX(26px);
        }

        .modals-container .tabs {
          display: flex;
          margin-bottom: 16px;
          border-bottom: 1px solid #ddd;
        }

        .modals-container .tab {
          padding: 8px 16px;
          border: none;
          background: none;
          cursor: pointer;
          color: #666;
        }

        .modals-container .tab.active {
          color: #007bff;
          border-bottom: 2px solid #007bff;
        }

        .modals-container .games-list {
          margin: 16px 0;
        }

        .modals-container .game-card {
          padding: 16px;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin-bottom: 8px;
          cursor: pointer;
        }

        .modals-container .game-card:hover {
          background: #f5f5f5;
        }

        .modals-container .game-card h3 {
          margin: 0 0 8px 0;
        }

        .modals-container .game-card p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .modals-container .pagination {
          display: flex;
          justify-content: center;
          gap: 12px;
          align-items: center;
          margin-top: 16px;
        }

        .modals-container .pagination button {
          padding: 8px 16px;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          cursor: pointer;
        }

        .modals-container .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modals-container .loading {
          text-align: center;
          padding: 20px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default GameModals;
